import React, { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { zodResolver } from '@hookform/resolvers/zod';
import {
  QButton,
  QCloseButton,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QSelectItem,
  QStack,
  QText,
  useToastProvider,
} from '@qualio/ui-components';

import {
  ProductRootCause,
  QualityEvent,
  EventTag,
  QualityEventCreate,
  QualityEventCreateWithTitleRestriction,
  User,
} from '../../types';
import { Relation } from '../../types/relation';
import EventForm from './components/EventForm';
import { ResourceSelectItem } from './components/MultiSelectWithSuggestions';
import { DefaultErrorMessage, EditEvent, EventUpdated, EventNotUpdated } from '../../displayStrings';
import { DateTime } from 'luxon';
import { formatDatePickerComponentDateToString } from '../../utils/datetimeUtils';
import { useEventMetadata } from '../../hooks/useEventMetadata';
import { linkedRegistryItemIds } from '../../utils/eventUtils';
import { useProducts } from '../../hooks/useProducts';
import { useRootCauses } from '../../hooks/useRootCauses';

type Props = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  event: QualityEvent;
  related: Relation[];
  suppliers: QSelectItem[];
  relatedData: {
    relatedDocuments: ResourceSelectItem[];
    relatedEvents: ResourceSelectItem[];
  };
  selectsData: {
    eventTags: EventTag[];
    users: User[];
  };
  onSave: (payload: QualityEventCreate) => Promise<void>;
};

const EventEditModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  selectsData,
  onSave,
  event,
  related,
  suppliers,
  relatedData,
}) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();
  const { data: eventsMetadata } = useEventMetadata();
  const { data: productsList } = useProducts(linkedRegistryItemIds(event.products));
  const { data: rootCausesList } = useRootCauses(linkedRegistryItemIds(event.rootcauses));
  const formMethods = useForm<QualityEventCreateWithTitleRestriction>({
    mode: 'onSubmit',
    resolver: zodResolver(QualityEventCreateWithTitleRestriction),
    defaultValues: {
      due_date: event.due_date
        ? event.due_date
        : formatDatePickerComponentDateToString(DateTime.now().plus({ days: event.time_limit }).toJSDate()),
      related_products: event.products?.map((product: ProductRootCause) => product.matrix_id),
      related_rootcauses: event.rootcauses?.map((rootcause: ProductRootCause) => rootcause.matrix_id),
    },
  });

  const {
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = formMethods;

  const formHasErrors = !!Object.keys(errors).length;

  const handleClose = () => {
    setIsOpen(false);
    reset();
  };

  const onSubmit = useCallback(
    async (data: QualityEventCreate) => {
      try {
        await onSave(data);
        setIsOpen(false);
        showToast({
          title: EventUpdated,
          description: `"${data.title}" was updated`,
          status: 'success',
          'data-cy': 'qe-event-update-success-toast',
        });
        reset();
      } catch {
        showToast({
          title: EventNotUpdated,
          description: DefaultErrorMessage,
          status: 'error',
        });
      }
    },
    [onSave, showToast, setIsOpen, reset],
  );

  return (
    <QModal isOpen={isOpen} onClose={handleClose} size="xl">
      <QModalHeader>
        <QText>{EditEvent}</QText>
        <QCloseButton onClick={handleClose} data-metrics="edit-event-close-modal-button" />
      </QModalHeader>
      <QModalBody>
        <QStack direction="column" color="gray.700" spacing={4}>
          <EventForm
            formMethods={formMethods}
            onSubmit={onSubmit}
            data={{
              ...selectsData,
              probabilities: eventsMetadata?.probabilities ?? [],
              severities: eventsMetadata?.severities ?? [],
              risks: eventsMetadata?.risks ?? [],
              products: productsList ?? [],
              rootCauses: rootCausesList ?? [],
              eventTypes: [],
            }}
            event={event}
            related={related}
            suppliers={suppliers}
            relatedData={relatedData}
            optionalFields={event.template_fields}
            isDueDateEditable={false}
          />
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton
          onClick={handleClose}
          variant="outline"
          data-metrics="edit-event-cancel-button"
          isDisabled={isSubmitting}
        >
          {t('common.actions.cancel')}
        </QButton>
        <QButton
          onClick={() => handleSubmit(onSubmit)()}
          isDisabled={formHasErrors || isSubmitting}
          isLoading={isSubmitting}
          data-cy="edit-event-button"
          data-metrics="edit-event-button"
        >
          {t('events.actions.editEvent')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};

export default EventEditModal;
